import { useEffect, useRef } from 'react';
import { useLocation } from '@remix-run/react';
import { useAtomValue } from 'jotai';
import { cn } from '@rocco/utils/cn';
import { uiContextAtomDisplayMode } from '@rocco/states/uiContextAtomDisplayMode';
import { FlexibleModuleEnum } from 'generated/graphql';
import type { PageBuilderModuleType } from '~/modules/page-builder/types';
import AdvertMediumRectangle from '~/modules/adverts/component/advert-medium-rectangle/AdvertMediumRectangle';
import AdvertBillboard from '~/modules/adverts/component/advert-billboard/AdvertBillboard';
import type { ModulesWithAds } from '~/modules/adverts/type';
import { getScrollParams } from '~/modules/page-builder/util/get-scroll-params';
import DetailSection from '../detail-section/DetailSection';
import DetailModule from './DetailModule';

interface DetailModuleProps {
    className?: string;
    modules: ModulesWithAds;
    articleGatingEnabled?: boolean;
}

/**
 * Rendered Page Builder modules on the website.
 */
const DetailModulesMobile = ({
    className,
    modules,
    articleGatingEnabled,
}: DetailModuleProps) => {
    const { isMobile } = useAtomValue(uiContextAtomDisplayMode);
    const location = useLocation();
    const hideAd = location.state?.hideAd;
    const urlSearchParams = new URLSearchParams(location.search);
    const urlVideoParam = urlSearchParams.get('video');
    let urlVideoIndex = urlVideoParam ? Number(urlVideoParam) : -1;
    let autoPlayVideoModuleId = '';
    // top banner is index 1
    if (urlVideoIndex > 1) {
        const allVideos = modules.filter(
            (m) => m.presetType === FlexibleModuleEnum.SingleVideoModule
        );
        const videoCount = allVideos.length;
        if (videoCount > 0) {
            if (urlVideoIndex > videoCount || urlVideoIndex < 1) {
                // default to first video is url has video=9000
                urlVideoIndex = 1;
            }
            autoPlayVideoModuleId = allVideos[urlVideoIndex - 1]!.id;
        }
    }

    // if article gating is on, then render the first two non ads modules
    const renderModules = articleGatingEnabled
        ? modules
              .filter(
                  (module) => module.presetType !== 'ads' || !('ad' in module)
              )
              .splice(0, 2)
        : modules;

    const hasScrolledToTagID = useRef(false);

    useEffect(() => {
        const { scrollToTagID } = getScrollParams(window?.location.search);
        if (scrollToTagID && !hasScrolledToTagID.current) {
            const moduleShouldScroll = modules.find(
                (m) =>
                    'columns' in m &&
                    m.columns.some((c) =>
                        c.uniqueApprovedTags?.some(
                            (t) => t.id === scrollToTagID
                        )
                    )
            );
            if (moduleShouldScroll) {
                const element = document.getElementById(moduleShouldScroll.id);
                if (element) {
                    element.scrollIntoView({ behavior: 'smooth' });
                    hasScrolledToTagID.current = true;
                }
            }
        }
    }, [modules]);

    const reorderedModules = (() => {
        const arr = [...renderModules];
        if (arr.length < 2) return arr;
        const idx = arr.findIndex(
            (m) =>
                m.presetType === FlexibleModuleEnum.FullPageImagesSliderModule
        );
        if (idx > -1 && idx !== 1) {
            const item = arr[idx];
            if (item) {
                arr.splice(idx, 1);
                arr.splice(1, 0, item);
            }
        }
        return arr;
    })();

    // Track index of non-ad modules for eager loading determination
    let moduleIndex = 0;

    return (
        <article
            className={cn(
                'relative grid grid-cols-1 gap-12 md:mx-auto md:ml-auto md:mr-0',
                articleGatingEnabled &&
                    'max-h-35 overflow-hidden blur-[1px] md:max-h-48',
                className
            )}
        >
            {reorderedModules.map((module) => {
                const isAd = module.presetType === 'ads' && 'ad' in module;
                const percentContentViewed =
                    'memberSortOrder' in module
                        ? (module.memberSortOrder + 1) / modules.length
                        : 0;
                if (isAd && hideAd) {
                    return null;
                }

                // Get current module index for non-ad modules (for eager loading)
                const currentModuleIndex = isAd ? -1 : moduleIndex++;

                return (
                    <div key={module.id}>
                        <DetailSection
                            isAd={isAd}
                            percentContentViewed={percentContentViewed}
                            moduleId={module.id}
                        >
                            {isAd ? (
                                isMobile ? (
                                    <AdvertMediumRectangle {...module.ad} />
                                ) : (
                                    <AdvertBillboard {...module.ad} />
                                )
                            ) : (
                                <div
                                    className={cn(
                                        '-ml-1 -mr-1 md:mx-auto',
                                        module.presetType ===
                                            FlexibleModuleEnum.QuadImageModule ||
                                            module.presetType ===
                                                FlexibleModuleEnum.OctaImageModule
                                            ? 'md:max-w-fit md:scale-[85%]'
                                            : ''
                                    )}
                                >
                                    <DetailModule
                                        module={module as PageBuilderModuleType}
                                        autoPlayVideoModuleId={
                                            autoPlayVideoModuleId
                                        }
                                        moduleIndex={currentModuleIndex}
                                    />
                                </div>
                            )}
                        </DetailSection>
                    </div>
                );
            })}
            {articleGatingEnabled && (
                <div className="absolute inset-0 bg-gradient-to-b from-gray-50/50 to-gray-50" />
            )}
        </article>
    );
};

export default DetailModulesMobile;
